import React, { useCallback, useState } from 'react';
import { useGetList, useLocale, useNotify, useRecordContext } from 'react-admin';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiFetch } from '../lib/apiFetch';
import { langOf, SIED_LABELS, siedAgo, siedDisplay } from './siedStatus';
import { SiedSidebarControls } from './SiedSignAction';

type SiedFields = {
  region?: string | null;
  sp3_number?: string | null;
  sied_status?: string | null;
  sied_status_raw?: string | null;
  sied_status_checked_at?: string | null;
  sied_status_changed_at?: string | null;
};

const PILL =
  'inline-flex h-6 max-w-full items-center rounded-md border px-1.5 text-[11px] font-medium whitespace-nowrap';

const isQuebec = (r: SiedFields | undefined) => (r?.region ?? '').toLowerCase() === 'quebec';

/**
 * The SIED step as a coloured pill. `compact` uses the short label (list
 * column); the tooltip always carries SIED's own FR text and the check time.
 */
export const SiedStatusPill: React.FC<{ record?: SiedFields; compact?: boolean }> = ({
  record: recordProp,
  compact,
}) => {
  const ctx = useRecordContext<SiedFields>();
  const record = recordProp ?? ctx;
  const lang = langOf(useLocale());
  const d = siedDisplay(record?.sied_status);

  if (!d) {
    // No SIED form linked: Ontario never has one; a QC case with no SP-3 yet
    // shows a dash so "not in SIED yet" reads differently from "n/a".
    return (
      <span
        className={cn(PILL, 'border-gray-200 text-muted-foreground/50')}
        title={isQuebec(record) ? (lang === 'fr' ? 'Aucun formulaire SIED lié' : 'No SIED form linked') : 'n/a'}
      >
        –
      </span>
    );
  }

  const title = [
    d.long[lang],
    record?.sied_status_raw ? `SIED : ${record.sied_status_raw}` : '',
    record?.sied_status_checked_at
      ? `${SIED_LABELS.checked[lang]} ${siedAgo(record.sied_status_checked_at, lang)}`
      : '',
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <span className={cn(PILL, d.cls)} title={title} data-sied-status={record?.sied_status}>
      <span className="truncate">{compact ? d.short[lang] : d.long[lang]}</span>
    </span>
  );
};

/**
 * Case sidebar row: "SIED: <label> · checked <time>" with a refresh button.
 *
 * The show page's record comes from `funerals` (getOne), which doesn't carry
 * the sied_status* columns — they're on funerals_searchable, which the data
 * provider uses for getList. So read this one case through getList.
 */
export const SiedStatusSidebarRow: React.FC = () => {
  const record = useRecordContext<SiedFields & { id: number }>();
  const lang = langOf(useLocale());
  const notify = useNotify();
  const [refreshing, setRefreshing] = useState(false);

  const { data, refetch } = useGetList<SiedFields & { id: number }>(
    'funerals',
    {
      filter: { id: record?.id },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'ASC' },
    },
    { enabled: !!record?.id && isQuebec(record) },
  );

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // Same run as "Check now" on the SIED page: one search covers every case.
      await apiFetch('sied/check', { method: 'POST', body: JSON.stringify({}) });
      const deadline = Date.now() + 3 * 60 * 1000;
      for (;;) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await apiFetch('sied/status');
        if (!s?.isRunning) {
          if (s?.lastError) notify(`${SIED_LABELS.refreshFailed[lang]}: ${s.lastError}`, { type: 'error' });
          break;
        }
        if (Date.now() > deadline) break;
      }
      await refetch();
    } catch {
      notify(SIED_LABELS.refreshFailed[lang], { type: 'error' });
    } finally {
      setRefreshing(false);
    }
  }, [lang, notify, refetch]);

  if (!record || !isQuebec(record)) return null;
  const row = data?.[0];

  // One row like the other Status rows: label left; pill, one action and
  // refresh right. Reasons, SIED's FR text and the check time are in the
  // pill's tooltip; when / who / proof are in the case feed.
  return (
    <div className="flex justify-between items-center py-1.5 gap-2">
      <span className="text-[13px] font-medium text-foreground shrink-0">SIED</span>
      <div className="flex min-w-0 items-center gap-1.5">
        <SiedSidebarControls
          funeralId={record.id}
          siedStatus={row?.sied_status ?? null}
          siedRaw={row?.sied_status_raw ?? null}
          checkedLine={
            row?.sied_status_checked_at
              ? `${SIED_LABELS.checked[lang]} ${siedAgo(row.sied_status_checked_at, lang)}`
              : SIED_LABELS.never[lang]
          }
          onChanged={() => void refetch()}
        />
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={refreshing}
          title={refreshing ? SIED_LABELS.refreshing[lang] : SIED_LABELS.refresh[lang]}
          aria-label={SIED_LABELS.refresh[lang]}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-60"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
        </button>
      </div>
    </div>
  );
};

export default SiedStatusPill;
